import { useCallback } from 'react';
import { generatePagedQuery } from 'utils/string';

import useFetch from 'hooks/useFetch';

import {
    APPLICATOR_CERTIFICATIONS_API,
    DEFAULT_PAGE_SIZE,
    getUserApplicatorCertificationsApi
} from './constants';
import type {
    ApplicatorCertificationPayload,
    ApplicatorCertificationResponse,
    PagedResponseApplicatorCertificationResponse
} from './types';

const useApplicatorCertifications = () => {
    const { authenticatedDelete, authenticatedGet, authenticatedPost, authenticatedPut } =
        useFetch();

    const fetchApplicatorCertifications = useCallback(
        async (pageToken?: string | null, pageSize = DEFAULT_PAGE_SIZE) => {
            const queryParameters = generatePagedQuery(pageToken, pageSize);

            const response = await authenticatedGet(
                `${APPLICATOR_CERTIFICATIONS_API}?${queryParameters.toString()}`,
                null
            );

            if (response.error) {
                throw new Error(JSON.stringify(response.error));
            }

            return response.data as PagedResponseApplicatorCertificationResponse;
        },
        [authenticatedGet]
    );

    const fetchUserApplicatorCertifications = useCallback(
        async (userId: string, pageToken?: string | null, pageSize = DEFAULT_PAGE_SIZE) => {
            const queryParameters = generatePagedQuery(pageToken, pageSize);

            const response = await authenticatedGet(
                `${getUserApplicatorCertificationsApi(userId)}?${queryParameters.toString()}`,
                null
            );

            if (response.error) {
                throw new Error(JSON.stringify(response.error));
            }

            return response.data as PagedResponseApplicatorCertificationResponse;
        },
        [authenticatedGet]
    );

    const fetchUserApplicatorCertification = useCallback(
        async (userId: string, certificationId: string) => {
            const response = await authenticatedGet(
                `${getUserApplicatorCertificationsApi(userId)}/${certificationId}`,
                null
            );

            if (response.error) {
                throw new Error(JSON.stringify(response.error));
            }

            return response.data as ApplicatorCertificationResponse;
        },
        [authenticatedGet]
    );

    const createUserApplicatorCertification = useCallback(
        async (userId: string, payload: ApplicatorCertificationPayload) => {
            const response = await authenticatedPost(
                getUserApplicatorCertificationsApi(userId),
                JSON.stringify(payload)
            );

            if (response.error) {
                throw new Error(JSON.stringify(response.error));
            }
        },
        [authenticatedPost]
    );

    const updateUserApplicatorCertification = useCallback(
        async (
            userId: string,
            certificationId: string,
            payload: ApplicatorCertificationPayload
        ) => {
            const response = await authenticatedPut(
                `${getUserApplicatorCertificationsApi(userId)}/${certificationId}`,
                JSON.stringify(payload)
            );

            if (response.error) {
                throw new Error(JSON.stringify(response.error));
            }
        },
        [authenticatedPut]
    );

    const fetchApplicatorCertification = useCallback(
        async (certificationId: string) => {
            const response = await authenticatedGet(
                `${APPLICATOR_CERTIFICATIONS_API}/${certificationId}`,
                null
            );

            if (response.error) {
                throw new Error(JSON.stringify(response.error));
            }

            return response.data as ApplicatorCertificationResponse;
        },
        [authenticatedGet]
    );

    const createApplicatorCertification = useCallback(
        async (payload: ApplicatorCertificationPayload) => {
            const response = await authenticatedPost(
                APPLICATOR_CERTIFICATIONS_API,
                JSON.stringify(payload)
            );

            if (response.error) {
                throw new Error(JSON.stringify(response.error));
            }
        },
        [authenticatedPost]
    );

    const updateApplicatorCertification = useCallback(
        async (certificationId: string, payload: ApplicatorCertificationPayload) => {
            const response = await authenticatedPut(
                `${APPLICATOR_CERTIFICATIONS_API}/${certificationId}`,
                JSON.stringify(payload)
            );

            if (response.error) {
                throw new Error(JSON.stringify(response.error));
            }
        },
        [authenticatedPut]
    );

    const deleteApplicatorCertification = useCallback(
        async (certificationId: string) => {
            const response = await authenticatedDelete(
                `${APPLICATOR_CERTIFICATIONS_API}/${certificationId}`,
                null
            );

            if (response.error) {
                throw new Error(JSON.stringify(response.error));
            }
        },
        [authenticatedDelete]
    );

    return {
        createApplicatorCertification,
        createUserApplicatorCertification,
        deleteApplicatorCertification,
        fetchApplicatorCertification,
        fetchApplicatorCertifications,
        fetchUserApplicatorCertification,
        fetchUserApplicatorCertifications,
        updateApplicatorCertification,
        updateUserApplicatorCertification
    };
};

export default useApplicatorCertifications;
